import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { IconDownload, IconLoader2 } from '@tabler/icons-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { PromoStateComparisonChart } from '@/components/spar-mobile/PromoStateComparisonChart';
import { PromoStateBadge } from '@/components/spar-mobile/PromoStateBadge';
import {
  StateComparisonRow,
  UpliftRow,
  ProductState,
  STATE_COLORS,
  ANALYSIS_START_DATE,
  analysisEndDate,
} from '@/types/promoAttribution';
import { formatPromoNumber } from '@/utils/promoFormatNumber';
import { exportElementToPdf } from '@/utils/exportPdf';

const OVERVIEW_STATE_ORDER: ProductState[] = [
  'Standard Price',
  'Promotion',
  'Promotion and Promo Data',
];

export function PromoAttributionOverviewPage() {
  const [stateData, setStateData] = useState<StateComparisonRow[]>([]);
  const [upliftData, setUpliftData] = useState<UpliftRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [stateRes, upliftRes] = await Promise.all([
        api.get('/spar-mobile/promo-attribution/state-comparison'),
        api.get('/spar-mobile/promo-attribution/top-uplift?limit=20'),
      ]);
      setStateData(stateRes.data.data ?? []);
      setUpliftData(upliftRes.data.data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const baseline = stateData.find((r) => r.state === 'Standard Price');
  const promotionRow = stateData.find((r) => r.state === 'Promotion');

  const handleExportPdf = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      await exportElementToPdf(printRef.current, {
        filename: `promo-attribution-overview-${date}.pdf`,
      });
    } catch (err) {
      console.error('PDF export failed', err);
      toast.error('Could not generate PDF', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setExporting(false);
    }
  };

  function upliftVsBaseline(row: StateComparisonRow | undefined) {
    if (!row || !baseline?.avg_daily_units) return null;
    return ((row.avg_daily_units - baseline.avg_daily_units) / baseline.avg_daily_units) * 100;
  }

  function upliftVsPromotion(row: StateComparisonRow | undefined) {
    if (!row || !promotionRow?.avg_daily_units) return null;
    return ((row.avg_daily_units - promotionRow.avg_daily_units) / promotionRow.avg_daily_units) * 100;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Promo Data Attribution Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Analysis window: {ANALYSIS_START_DATE} → {analysisEndDate()}
          </p>
        </div>
        <button
          onClick={handleExportPdf}
          disabled={exporting || stateData.length === 0}
          className="self-end sm:self-auto inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting
            ? <IconLoader2 size={14} className="animate-spin" />
            : <IconDownload size={14} />}
          {exporting ? 'Generating…' : 'Export PDF'}
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div ref={printRef} className="space-y-6 bg-background">
      <div className="flex gap-4 flex-wrap">
        {OVERVIEW_STATE_ORDER.map((s) => (
          <span key={s} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-block w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: STATE_COLORS[s] }} />
            {s}
          </span>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        % vs standard / vs promotion = <span className="font-mono">(state avg − reference avg) ÷ reference avg × 100</span>
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {OVERVIEW_STATE_ORDER.map((state) => {
          const row = stateData.find((r) => r.state === state);
          const upliftStd = upliftVsBaseline(row);
          const upliftPromo = state === 'Promotion and Promo Data' ? upliftVsPromotion(row) : null;
          return (
            <div key={state} className="rounded-lg border bg-card p-5 space-y-1">
              <PromoStateBadge state={state} />
              <p className="text-3xl font-bold tabular-nums">
                {row ? formatPromoNumber(row.avg_daily_units, 1) : loading ? '…' : '—'}
              </p>
              <p className="text-xs text-muted-foreground">avg daily units</p>
              {upliftStd != null && state !== 'Standard Price' && (
                <p className={cn('text-xs font-semibold', upliftStd >= 0 ? 'text-emerald-600' : 'text-red-500')}>
                  {upliftStd >= 0 ? '+' : ''}{formatPromoNumber(upliftStd, 1)}% vs standard
                </p>
              )}
              {upliftPromo != null && (
                <p className={cn('text-xs font-semibold', upliftPromo >= 0 ? 'text-emerald-600' : 'text-red-500')}>
                  {upliftPromo >= 0 ? '+' : ''}{formatPromoNumber(upliftPromo, 1)}% vs promotion
                </p>
              )}
              {row && (
                <p className="text-xs text-muted-foreground">
                  {formatPromoNumber(row.distinct_products)} products · {formatPromoNumber(row.distinct_calendar_days)} days
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border bg-card">
          <div className="p-4 pb-2">
            <p className="text-base font-semibold">Avg Daily Units by State</p>
          </div>
          <div className="p-4 pt-0">
            {stateData.length > 0 ? (
              <PromoStateComparisonChart data={stateData} metric="avg_daily_units" states={OVERVIEW_STATE_ORDER} />
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
                {loading ? 'Loading…' : 'No data'}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border bg-card">
          <div className="p-4 pb-2">
            <p className="text-base font-semibold">State Summary</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Days = distinct calendar days where at least one product had that state.
            </p>
          </div>
          <div className="p-4 pt-0">
            {stateData.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="pb-2 font-medium">State</th>
                    <th className="pb-2 font-medium text-right">Products</th>
                    <th className="pb-2 font-medium text-right">Days</th>
                    <th className="pb-2 font-medium text-right">Avg Daily Units</th>
                    <th className="pb-2 font-medium text-right">Total Units</th>
                  </tr>
                </thead>
                <tbody>
                  {OVERVIEW_STATE_ORDER.map((state) => {
                    const row = stateData.find((r) => r.state === state);
                    if (!row) return null;
                    return (
                      <tr key={state} className="border-b last:border-0">
                        <td className="py-2"><PromoStateBadge state={state} /></td>
                        <td className="py-2 text-right">{formatPromoNumber(row.distinct_products)}</td>
                        <td className="py-2 text-right">{formatPromoNumber(row.distinct_calendar_days)}</td>
                        <td className="py-2 text-right font-medium">{formatPromoNumber(row.avg_daily_units, 1)}</td>
                        <td className="py-2 text-right">{formatPromoNumber(row.total_units)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
                {loading ? 'Loading…' : 'No data'}
              </div>
            )}
          </div>
        </div>
      </div>

      <hr />

      <div>
        <h2 className="text-lg font-semibold tracking-tight mb-1">Top Products by Promo Data Uplift</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Products with ≥3 days in each state. Uplift = avg daily units during Promo Data vs without.
        </p>
        <div className="rounded-lg border bg-card overflow-hidden">
          {upliftData.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2 font-medium w-8">#</th>
                  <th className="px-4 py-2 font-medium">GTIN</th>
                  <th className="px-4 py-2 font-medium">Product</th>
                  <th className="px-4 py-2 font-medium text-right">Avg Units (w/ Promo Data)</th>
                  <th className="px-4 py-2 font-medium text-right">Avg Units (w/o Promo Data)</th>
                  <th className="px-4 py-2 font-medium text-right">Uplift %</th>
                  <th className="px-4 py-2 font-medium text-right">Days w/ Promo</th>
                  <th className="px-4 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {upliftData.map((row, i) => (
                  <tr key={row.gtin} className="border-t">
                    <td className="px-4 py-2 text-muted-foreground">{i + 1}</td>
                    <td className="px-4 py-2 font-mono text-sm">{row.gtin}</td>
                    <td className="px-4 py-2 text-sm max-w-[180px] truncate" title={row.description ?? undefined}>
                      {row.description ?? <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right">{formatPromoNumber(row.avg_units_promo_data, 1)}</td>
                    <td className="px-4 py-2 text-right">{formatPromoNumber(row.avg_units_no_promo_data, 1)}</td>
                    <td className={cn('px-4 py-2 text-right font-semibold tabular-nums', row.uplift_pct >= 0 ? 'text-emerald-600' : 'text-red-500')}>
                      {row.uplift_pct >= 0 ? '+' : ''}{formatPromoNumber(row.uplift_pct, 1)}%
                    </td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{formatPromoNumber(row.days_with_promo_data)}</td>
                    <td className="px-4 py-2">
                      <Link
                        to={`/spar-mobile/promo-attribution/products/${row.gtin}`}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="h-24 flex items-center justify-center text-muted-foreground text-sm">
              {loading ? 'Loading…' : 'No data'}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
